import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { type components } from '@heidi/schema/ml-scribe';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRef } from 'react';
import { postUploadSessionSchedule } from '../../../data/api/session';
import { type SessionListHost } from '../../session-list/session-list-host';

type ErrorResponse = components['schemas']['ErrorResponse'];

export const useMutateUploadSessionSchedule = (host: SessionListHost) => {
  const { t } = useTranslations();
  const startTimeRef = useRef<number>(0);

  return useMutation({
    mutationKey: ['uploadSessionSchedule'],
    mutationFn: async ({ file, signal }: { file: File; signal?: AbortSignal }) => {
      const response = await postUploadSessionSchedule(file, signal);
      return response.data;
    },
    onMutate: () => {
      startTimeRef.current = performance.now();
    },
    onSuccess: (data) => {
      const processingDurationMs =
        startTimeRef.current > 0 ? performance.now() - startTimeRef.current : 0;

      if (data.sessions.length === 0) {
        const errorMessage = t('sessions.upcomingSessions.noValidSessionDataFound');
        host.analytics.trackScheduleUploadProcessingFailed({
          error_message: errorMessage,
          error_code: data.error ?? 'no_valid_sessions',
        });
      } else {
        host.analytics.trackScheduleUploadCompleted({
          num_processed: data.sessions.length,
          processing_duration_ms: processingDurationMs,
        });
      }

      startTimeRef.current = 0;
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.code !== 'ERR_CANCELED') {
        const errorData = error.response?.data as ErrorResponse | undefined;
        // Surface a generic message to users — backend `detail.msg` can contain
        // raw validation errors (e.g. pydantic stack traces) that are noisy and
        // leak implementation detail. The full message is still forwarded to
        // analytics so engineers can diagnose failures.
        toast({
          tone: 'destructive',
          title: t('sessions.upcomingSessions.noValidSessionDataFound'),
        });
        host.analytics.trackScheduleUploadProcessingFailed({
          error_message: errorData?.detail?.msg || t('common.somethingWentWrong'),
          error_code:
            error.response?.status != null ? `http_${error.response.status}` : error.code,
        });
      }
      startTimeRef.current = 0;
    },
  });
};
